import * as React from "react";
import {
  DatePicker,
  DefaultButton,
  IComboBox,
  IComboBoxStyles,
  IDatePickerStyles,
  IIconProps,
  ITextFieldStyles,
  TimePicker,
  TimePickerValidationResultData,
  notifyHostChanged,
} from "@fluentui/react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import "./Schedule.css";
import ScheduleIcon from "assets/svg/schedule-icon";
import ReactDOM from "react-dom";

interface Props {
  items: ScheduleDataSource[];
  onItemsChanged(scheduleItems: ScheduleDataSource[]);
}

export interface ScheduleDataSource {
  date: Date;
  startTime: Date;
  endTime: Date;
  dateHasError: boolean;
  startTimeHasError: boolean;
  errorMessage: string;
}

const Schedule: React.FunctionComponent<Props> = (props: Props) => {
  const onItemsChanged = props.onItemsChanged;
  const [scheduleItems, setScheduleItems] = useState<ScheduleDataSource[]>(props.items);
  const [keyIndexSuffix, setKeyIndexSuffix] = useState<number>(0);

  const dateAnchor = new Date("February 1, 2024 00:00:00");

  useEffect(() => {
    if (scheduleItems?.length < 1) {
      const items: ScheduleDataSource[] = [{} as ScheduleDataSource];
      setScheduleItems(items);
    }
  }, []);

  const Child = forwardRef((props, ref) => {
    useImperativeHandle(ref, (): boolean => {
      return true;
    });

    return <h1>Child</h1>;
  });

  const changeScheduleItemsState = (notValidateIndex?: number) => {
    const validatedItems = validateScheduleItem(notValidateIndex);
    setScheduleItems(validatedItems);
    onItemsChanged(validatedItems);
  };

  const validateScheduleItem = (notValidateIndex?: number): ScheduleDataSource[] => {
    const validatedItems = scheduleItems.map((el, index) => {
      if (index === notValidateIndex) return el;
      el.startTimeHasError = false;
      el.dateHasError = false;

      if (!el.date) {
        el.dateHasError = true;
        el.errorMessage = "Date cannot be empty!";
        return el;
      }
      if (!el.startTime) {
        el.startTimeHasError = true;
        el.errorMessage = "Start time cannot be empty!";
        return el;
      }
      if (el.endTime && el.startTime >= el.endTime) {
        el.startTimeHasError = true;
        el.errorMessage = "The start time cannot be greater than the end time!";
        return el;
      }

      return el;
    });

    return validatedItems;
  };

  const addDate = () => {
    scheduleItems.push({ date: null, startTime: null, endTime: null } as ScheduleDataSource);
    changeScheduleItemsState(scheduleItems.length - 1);
  };

  const removeDate = (index: number) => {
    setKeyIndexSuffix(keyIndexSuffix + 1);
    scheduleItems.splice(index, 1);
    changeScheduleItemsState();
  };

  const changeDate = (index: number, newDate: Date) => {
    scheduleItems[index].date = newDate;
    changeScheduleItemsState();
  };

  const changeStartTime = (index: number, newDate: Date) => {
    scheduleItems[index].startTime = newDate;
    changeScheduleItemsState();
  };

  const changeEndTime = (index: number, newDate: Date) => {
    scheduleItems[index].endTime = newDate;
    changeScheduleItemsState();
  };

  const addDateButtonStyle: IIconProps = {
    iconName: "plusCustomIcon",
    styles: {
      root: {
        position: "relative",
        left: "5rem",
        bottom: "0.125rem",
      },
    },
  };

  const dateIconProps: IIconProps = {
    iconName: "CalendarIcon",
    styles: {
      root: {
        bottom: "0.063rem",
      },
    },
  };

  const timeIconProps: IIconProps = {
    iconName: "clockIcon",
    styles: {
      root: {
        position: "relative",
        bottom: "0.125rem",
      },
    },
  };

  const getCbxBaseStyle = (hasError: boolean) => {
    const baseCbxStyle = {
      backgroundColor: "#f2f3f6",
      ":hover": {
        border: "none",
        borderRadius: "4px 4px 0 0",
      },
      ":after": {
        border: "none",
        borderRadius: "4px 4px 0 0",
        borderBottom: `1px solid ${hasError ? "#ba1b1b" : "#48454E"}`,
      },
    };
    return baseCbxStyle;
  };

  const cbxStyles = (hasError: boolean): Partial<IComboBoxStyles> => {
    const baseStyle = getCbxBaseStyle(hasError);

    const cbxStyle = {
      container: {},
      root: {
        ...baseStyle,
        height: "2.5rem",
      },
      input: { ...baseStyle },
      optionsContainerWrapper: {},
    };
    return cbxStyle;
  };

  const generateKey = (index: number, childNumber: number): string => {
    const key = (index + childNumber + keyIndexSuffix).toString();
    return key;
  };

  return (
    <>
      <div className="schedule-main-container">
        <div>
          <ScheduleIcon></ScheduleIcon>
          <span className="schedule-header-text"> Schedule</span>
        </div>
        {scheduleItems?.map((dt, index) => (
          <div key={generateKey(index, 1)}>
            <div key={generateKey(index, 2)} className="date-time-main-container">
              <DatePicker
                key={generateKey(index, 3)}
                value={dt.date}
                placeholder="Enter Date *"
                ariaLabel="Select a date"
                onSelectDate={(selectedDate) => {
                  changeDate(index, selectedDate);
                }}
                textField={{
                  iconProps: dateIconProps,
                  styles: {
                    fieldGroup: {
                      backgroundColor: "#f2f3f6",
                      height: "2.5rem",
                      paddingTop: "0.375rem",
                      border: "0",
                      borderRadius: "4px 4px 0 0",
                      borderBottom: `1px solid ${dt.dateHasError ? "#ba1b1b" : "#48454E"}`,
                      ":hover": {
                        border: "none",
                        borderRadius: "4px 4px 0 0",
                        borderBottom: `1px solid ${dt.dateHasError ? "#ba1b1b" : "#48454E"}`,
                      },
                      ":after": {
                        border: "0",
                        borderBottom: `1px solid ${dt.dateHasError ? "#ba1b1b" : "#48454E"}`,
                        outline: "none",
                      },
                    },
                  },
                }}
              />
              <div key={generateKey(index, 4)} className="left-time-div">
                <TimePicker
                  key={generateKey(index, 5)}
                  dateAnchor={dateAnchor}
                  value={dt.startTime}
                  placeholder="Start time *"
                  styles={cbxStyles(dt.startTimeHasError)}
                  useHour12
                  buttonIconProps={timeIconProps}
                  increments={15}
                  allowFreeform={false}
                  autoComplete="on"
                  onChange={(ev, time) => {
                    changeStartTime(index, time);
                  }}
                />
              </div>
              <div key={generateKey(index, 6)} className="middle-time-div">
                to
              </div>
              <div key={generateKey(index, 7)} className="right-time--div">
                <TimePicker
                  key={generateKey(index, 8)}
                  dateAnchor={dateAnchor}
                  styles={cbxStyles(false)}
                  value={dt.endTime}
                  placeholder="End time"
                  useHour12
                  increments={15}
                  buttonIconProps={timeIconProps}
                  allowFreeform={false}
                  autoComplete="on"
                  onChange={(ev, time) => {
                    changeEndTime(index, time);
                  }}
                />
              </div>

              {scheduleItems?.length > 1 && (
                <DefaultButton
                  key={generateKey(index, 9)}
                  className="remove-btn"
                  text="Remove"
                  onClick={() => removeDate(index)}
                />
              )}
            </div>
          </div>
        ))}

        <DefaultButton
          className="add-date-btn"
          iconProps={addDateButtonStyle}
          toggle
          text="Add date"
          onClick={() => addDate()}
        />
      </div>
    </>
  );
};

export default Schedule;
